import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Form } from "react-bootstrap";
import axios from "axios";
import { useQuery } from "react-query";
import ContentHeader from "../../components/Admin/ContentHeader";
import CustomButton from "../../components/Admin/CustomButton/CustomButton";
import BranchQueueFilter from "../../components/Admin/BranchQueueFilter/BranchQueueFilter";
import { BranchAPI } from "../../constants/API";
import { commonHeaders } from "../../constants/HeadersAPI";
import { BranchQueueItemDto } from "../../hooks/Admin/WorkingPlacesTable/useGetQueuesForCurrentUser";
import { useAdminQueueIdsFilter } from "../../hooks/Admin/useAdminQueueIdsFilter";
import { buildRatingPagePath } from "../../constants/routes";
import { useGetRatingSettings } from "../../hooks/Admin/RatingSettings/useGetRatingSettings";
import { usePutRatingSettings } from "../../hooks/Admin/RatingSettings/usePutRatingSettings";
import { usePostRatingMedia } from "../../hooks/Admin/RatingSettings/usePostRatingMedia";
import { useDeleteRatingMedia } from "../../hooks/Admin/RatingSettings/useDeleteRatingMedia";
import { useMyPermissions } from "../../hooks/useMyPermissions";
import { baseSoundURL } from "../../config";
import {
  RatingBackgroundType,
  RatingMediaDto,
  RatingSettingsDto,
  RatingTicketPhase,
  RatingType,
  IN_SERVICE_TICKET_PHASES,
  RATING_TICKET_PHASES,
  sanitizeInServicePhases,
  sanitizeScreensaverPhases,
} from "../../types/Rating/RatingTypes";
import { parseNonNegativeIntegerInput } from "../../utils/Rating/parseNonNegativeIntegerInput";
import classes from "./RatingSettingsPage.module.scss";

const defaultForm: Omit<
  RatingSettingsDto,
  "branchQueueId" | "backgrounds" | "screensaverMedia"
> = {
  ratingType: "STARS",
  textColor: "#ffffff",
  transparentBackground: false,
  backgroundType: "COLOR",
  backgroundColor: "#1a1a2e",
  backgroundMediaId: null,
  backgroundMediaUrl: null,
  showQr: false,
  showOperatorPhoto: false,
  qrUrl: "",
  qrLabelText: "QR код для прохождения опроса",
  ratingTimeoutSeconds: 60,
  extensionCountdownSeconds: 10,
  thankYouSeconds: 10,
  screensaverIdleSeconds: 120,
  screensaverImageSeconds: 8,
  inServicePhases: ["CALL", "IN_PROGRESS"],
  ratingPhases: ["COMPLETION"],
  screensaverPhases: ["CALL", "IN_PROGRESS"],
  ratingPromptText: "Пожалуйста, оцените качество обслуживания оператором",
  ratingScaleHint: "(1-плохо, 5-отлично)",
  thankYouText: "Спасибо за голос!",
};

const RatingSettingsPage = () => {
  const [searchParams] = useSearchParams();
  const { canEdit } = useMyPermissions();
  const canEditRating = canEdit("SETTINGS_RATING");

  const [filterBranchIds, setFilterBranchIds] = useState<number[]>([]);
  const [filterQueueIds, setFilterQueueIds] = useState<number[]>([]);
  const [previewCabinet, setPreviewCabinet] = useState("1");
  const [previewWindowNum, setPreviewWindowNum] = useState("1");
  const [form, setForm] = useState(defaultForm);
  const [screensaverIds, setScreensaverIds] = useState<number[]>([]);
  const [screensaverDragIndex, setScreensaverDragIndex] = useState<
    number | null
  >(null);
  const [screensaverMediaCache, setScreensaverMediaCache] = useState<
    Record<number, RatingMediaDto>
  >({});
  const screensaverIdsDirtyRef = useRef(false);

  const { queueIdsFilter } = useAdminQueueIdsFilter();
  const hasSelectedQueue = filterQueueIds.some((id) => id > 0);
  const selectedBranchId = filterBranchIds[0] ?? null;

  useEffect(() => {
    const branchIdRaw = searchParams.get("branchId");
    if (branchIdRaw != null && branchIdRaw !== "") {
      const branchIdParam = Number(branchIdRaw);
      if (Number.isFinite(branchIdParam) && branchIdParam > 0) {
        setFilterBranchIds([branchIdParam]);
      }
    }
    const queueIdRaw = searchParams.get("branchQueueId");
    if (queueIdRaw != null && queueIdRaw !== "") {
      const queueIdParam = Number(queueIdRaw);
      if (Number.isFinite(queueIdParam) && queueIdParam > 0) {
        setFilterQueueIds([queueIdParam]);
      }
    }
  }, [searchParams]);

  const { data: branchQueues = [] } = useQuery(
    ["branchQueues", selectedBranchId],
    () =>
      axios
        .get<
          BranchQueueItemDto[]
        >(BranchAPI.getQueues(selectedBranchId!), commonHeaders())
        .then((r) => r.data),
    { enabled: selectedBranchId != null },
  );

  const branchQueueId = hasSelectedQueue
    ? (filterQueueIds.find((id) => id > 0) ?? null)
    : null;
  const selectedQueueName =
    branchQueueId != null
      ? (branchQueues.find((q) => q.id === branchQueueId)?.queueName ?? "")
      : "";
  const { data: settings, isLoading } = useGetRatingSettings(branchQueueId);
  const putSettings = usePutRatingSettings(branchQueueId);
  const postMedia = usePostRatingMedia(branchQueueId);
  const deleteMedia = useDeleteRatingMedia(branchQueueId);

  useEffect(() => {
    screensaverIdsDirtyRef.current = false;
    setScreensaverDragIndex(null);
    setScreensaverMediaCache({});
  }, [branchQueueId]);

  useEffect(() => {
    if (!settings) {
      return;
    }
    setForm({
      ratingType: settings.ratingType,
      textColor: settings.textColor,
      transparentBackground: settings.transparentBackground,
      backgroundType: settings.backgroundType,
      backgroundColor: settings.backgroundColor,
      backgroundMediaId: settings.backgroundMediaId,
      backgroundMediaUrl: settings.backgroundMediaUrl,
      showQr: settings.showQr,
      showOperatorPhoto: settings.showOperatorPhoto ?? false,
      qrUrl: settings.qrUrl ?? "",
      qrLabelText: settings.qrLabelText ?? defaultForm.qrLabelText,
      ratingTimeoutSeconds: settings.ratingTimeoutSeconds,
      extensionCountdownSeconds: settings.extensionCountdownSeconds,
      thankYouSeconds: settings.thankYouSeconds,
      screensaverIdleSeconds: settings.screensaverIdleSeconds,
      screensaverImageSeconds: settings.screensaverImageSeconds ?? 8,
      inServicePhases: sanitizeInServicePhases(
        settings.inServicePhases ?? ["CALL", "IN_PROGRESS"],
      ),
      ratingPhases: settings.ratingPhases ?? ["COMPLETION"],
      screensaverPhases: sanitizeScreensaverPhases(
        settings.screensaverPhases ?? ["CALL", "IN_PROGRESS"],
      ),
      ratingPromptText: settings.ratingPromptText ?? "",
      ratingScaleHint: settings.ratingScaleHint ?? "",
      thankYouText: settings.thankYouText ?? "",
    });
    if (!screensaverIdsDirtyRef.current) {
      setScreensaverIds(settings.screensaverMedia?.map((m) => m.id) ?? []);
    }
  }, [settings]);

  const screensaverMediaById = useMemo(() => {
    const map = new Map<number, RatingMediaDto>();
    settings?.screensaverMedia?.forEach((m) => map.set(m.id, m));
    settings?.backgrounds?.forEach((m) => map.set(m.id, m));
    Object.values(screensaverMediaCache).forEach((m) => map.set(m.id, m));
    return map;
  }, [settings, screensaverMediaCache]);

  const orderedScreensaverMedia = useMemo(
    () =>
      screensaverIds
        .map((id) => screensaverMediaById.get(id))
        .filter((m): m is RatingMediaDto => m != null),
    [screensaverIds, screensaverMediaById],
  );

  const ratingLink = useMemo(() => {
    if (!hasSelectedQueue || selectedBranchId == null || !selectedQueueName) {
      return "";
    }
    const cabinetRaw = previewCabinet.trim();
    const windowRaw = previewWindowNum.trim();
    const cabinet =
      cabinetRaw && cabinetRaw !== "-" ? Number(cabinetRaw) : null;
    const windowNum = windowRaw && windowRaw !== "-" ? Number(windowRaw) : null;
    if (
      (cabinet !== null && !Number.isFinite(cabinet)) ||
      (windowNum !== null && !Number.isFinite(windowNum))
    ) {
      return "";
    }
    const path = buildRatingPagePath({
      branch: selectedBranchId,
      queue: selectedQueueName,
      cabinet: Number.isFinite(cabinet as number) ? cabinet : null,
      window: Number.isFinite(windowNum as number) ? windowNum : null,
    });
    return `${globalThis.window?.location?.origin ?? ""}${path}`;
  }, [
    hasSelectedQueue,
    selectedBranchId,
    selectedQueueName,
    previewCabinet,
    previewWindowNum,
  ]);

  const togglePhase = (
    field: "screensaverPhases",
    phase: RatingTicketPhase,
  ) => {
    setForm((prev) => {
      const current = prev[field] ?? [];
      const next = current.includes(phase)
        ? current.filter((p) => p !== phase)
        : [...current, phase];
      return { ...prev, [field]: next };
    });
  };

  const getPhaseScreen = (
    phase: RatingTicketPhase,
  ): "in-service" | "rating" | null => {
    if (form.inServicePhases?.includes(phase)) {
      return "in-service";
    }
    if (form.ratingPhases?.includes(phase)) {
      return "rating";
    }
    return null;
  };

  const setPhaseScreen = (
    phase: RatingTicketPhase,
    screen: "in-service" | "rating",
  ) => {
    setForm((prev) => ({
      ...prev,
      inServicePhases:
        screen === "in-service" && phase !== "COMPLETION"
          ? [
              ...(prev.inServicePhases ?? []).filter((p) => p !== phase),
              phase,
            ]
          : (prev.inServicePhases ?? []).filter((p) => p !== phase),
      ratingPhases:
        screen === "rating"
          ? [...(prev.ratingPhases ?? []).filter((p) => p !== phase), phase]
          : (prev.ratingPhases ?? []).filter((p) => p !== phase),
    }));
  };

  const clearPhaseScreen = (phase: RatingTicketPhase) => {
    setForm((prev) => ({
      ...prev,
      inServicePhases: (prev.inServicePhases ?? []).filter((p) => p !== phase),
      ratingPhases: (prev.ratingPhases ?? []).filter((p) => p !== phase),
    }));
  };

  const handlePhaseScreenChange = (
    phase: RatingTicketPhase,
    screen: "in-service" | "rating",
    checked: boolean,
  ) => {
    if (checked) {
      setPhaseScreen(phase, screen);
    } else {
      clearPhaseScreen(phase);
    }
  };

  const setNonNegativeIntegerField = <
    K extends
      | "ratingTimeoutSeconds"
      | "extensionCountdownSeconds"
      | "thankYouSeconds"
      | "screensaverIdleSeconds"
      | "screensaverImageSeconds",
  >(
    field: K,
    raw: string,
  ) => {
    const parsed = parseNonNegativeIntegerInput(raw);
    if (parsed === null) {
      return;
    }
    setForm((prev) => ({ ...prev, [field]: parsed }));
  };

  const handleSave = async () => {
    if (!branchQueueId) {
      return;
    }
    const { backgroundMediaUrl: _backgroundMediaUrl, ...settingsPayload } =
      form;
    await putSettings.mutateAsync({
      ...settingsPayload,
      inServicePhases: sanitizeInServicePhases(settingsPayload.inServicePhases),
      screensaverPhases: sanitizeScreensaverPhases(settingsPayload.screensaverPhases),
      screensaverMediaIds: screensaverIds,
    });
    screensaverIdsDirtyRef.current = false;
  };

  const handleUpload = async (
    file: File | null,
    purpose: "BACKGROUND" | "SCREENSAVER",
  ) => {
    if (!file || !branchQueueId) {
      return;
    }
    const response = await postMedia.mutateAsync({ file, purpose });
    const media = response.data;
    if (purpose === "BACKGROUND") {
      setForm((prev) => ({
        ...prev,
        backgroundType: "IMAGE" as RatingBackgroundType,
        backgroundMediaId: media.id,
        backgroundMediaUrl: media.url,
      }));
    } else {
      screensaverIdsDirtyRef.current = true;
      setScreensaverMediaCache((prev) => ({ ...prev, [media.id]: media }));
      setScreensaverIds((prev) =>
        prev.includes(media.id) ? prev : [...prev, media.id],
      );
    }
  };

  const handleDeleteScreensaver = async (mediaId: number) => {
    if (!branchQueueId) {
      return;
    }
    await deleteMedia.mutateAsync(mediaId);
    screensaverIdsDirtyRef.current = true;
    setScreensaverIds((prev) => prev.filter((id) => id !== mediaId));
    setScreensaverMediaCache((prev) => {
      const next = { ...prev };
      delete next[mediaId];
      return next;
    });
  };

  const moveScreensaver = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) {
      return;
    }
    screensaverIdsDirtyRef.current = true;
    setScreensaverIds((prev) => {
      const next = [...prev];
      const [removed] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, removed);
      return next;
    });
  };

  const mediaUrl = (url: string) =>
    /^https?:\/\//i.test(url)
      ? url
      : `${baseSoundURL}${url.startsWith("/") ? "" : "/"}${url}`;

  return (
    <div className={classes.container}>
      <ContentHeader
        name="Настройки оценки качества"
        filterContent={
          <BranchQueueFilter
            selectedBranchIds={filterBranchIds}
            selectedQueueIds={filterQueueIds}
            onBranchChange={setFilterBranchIds}
            onQueueChange={setFilterQueueIds}
            singleQueue
            queueIdsFilter={queueIdsFilter}
          />
        }
      />

      {!hasSelectedQueue && (
        <div className={classes.section}>
          <p className={classes.noQueueSelectedHint}>
            Выберите очередь в фильтре, чтобы настроить оценку качества.
          </p>
        </div>
      )}

      {hasSelectedQueue && branchQueueId && (
        <>
          <div className={classes.section}>
            <h5>Статусы талона</h5>
            <p className={classes.hint}>
              Для каждого этапа выберите один экран, который показывается на
              планшете.
            </p>
            <div className={classes.phaseTableWrap}>
              <table className={classes.phaseTable}>
                <thead>
                  <tr>
                    <th>Этап обслуживания</th>
                    <th>Экран «Обслуживание в процессе»</th>
                    <th>Экран «Форма оценки качества»</th>
                  </tr>
                </thead>
                <tbody>
                  {RATING_TICKET_PHASES.map(({ value, label, hint }) => {
                    const inServiceApplicable = value !== "COMPLETION";
                    const selectedScreen = getPhaseScreen(value);
                    return (
                      <tr key={value}>
                        <td>
                          <span className={classes.phaseLabel}>{label}</span>
                          <span className={classes.phaseHint}> — {hint}</span>
                        </td>
                        <td className={classes.phaseCheckCell}>
                          {inServiceApplicable ? (
                            <Form.Check
                              type="checkbox"
                              id={`in-service-${value}`}
                              aria-label={`${label} — обслуживание в процессе`}
                              checked={selectedScreen === "in-service"}
                              disabled={!canEditRating}
                              onChange={(e) =>
                                handlePhaseScreenChange(
                                  value,
                                  "in-service",
                                  e.target.checked,
                                )
                              }
                            />
                          ) : (
                            <span className={classes.phaseNotApplicable}>—</span>
                          )}
                        </td>
                        <td className={classes.phaseCheckCell}>
                          <Form.Check
                            type="checkbox"
                            id={`rating-${value}`}
                            aria-label={`${label} — форма оценки качества`}
                            checked={selectedScreen === "rating"}
                            disabled={!canEditRating}
                            onChange={(e) =>
                              handlePhaseScreenChange(
                                value,
                                "rating",
                                e.target.checked,
                              )
                            }
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className={classes.section}>
            <h5>Тип оценки</h5>
            <Form.Select
              value={form.ratingType}
              disabled={!canEditRating}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  ratingType: e.target.value as RatingType,
                }))
              }
            >
              <option value="STARS">Звёзды</option>
              <option value="HEARTS">Сердечки</option>
              <option value="NUMBERS">Цифры 1–5</option>
            </Form.Select>
          </div>

          <div className={classes.section}>
            <h5>Оператор</h5>
            <Form.Check
              type="switch"
              label="Показывать фото оператора"
              checked={form.showOperatorPhoto}
              disabled={!canEditRating}
              onChange={(e) =>
                setForm((p) => ({ ...p, showOperatorPhoto: e.target.checked }))
              }
            />
            <p className={classes.hint}>
              На экране оценки отображаются имя и фото оператора. Если фото не
              загружено в карточке сотрудника, показывается шаблон.
            </p>
          </div>

          <div className={classes.section}>
            <h5>Тексты на экране</h5>
            <Form.Group className="mb-3">
              <Form.Label>Призыв к оценке</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.ratingPromptText}
                disabled={!canEditRating}
                onChange={(e) =>
                  setForm((p) => ({ ...p, ratingPromptText: e.target.value }))
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Подсказка шкалы</Form.Label>
              <Form.Control
                value={form.ratingScaleHint}
                disabled={!canEditRating}
                onChange={(e) =>
                  setForm((p) => ({ ...p, ratingScaleHint: e.target.value }))
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Экран благодарности</Form.Label>
              <Form.Control
                value={form.thankYouText}
                disabled={!canEditRating}
                onChange={(e) =>
                  setForm((p) => ({ ...p, thankYouText: e.target.value }))
                }
              />
            </Form.Group>
          </div>

          <div className={classes.section}>
            <h5>Оформление</h5>
            <div className={classes.row}>
              <Form.Label>Цвет текста</Form.Label>
              <Form.Control
                type="color"
                value={form.textColor}
                disabled={!canEditRating}
                onChange={(e) =>
                  setForm((p) => ({ ...p, textColor: e.target.value }))
                }
              />
            </div>
            <Form.Check
              type="switch"
              label="Размытый задний фон"
              checked={!form.transparentBackground}
              disabled={!canEditRating}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  transparentBackground: !e.target.checked,
                }))
              }
            />
            <div className={classes.row}>
              <Form.Select
                value={form.backgroundType}
                disabled={!canEditRating}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    backgroundType: e.target.value as RatingBackgroundType,
                  }))
                }
              >
                <option value="COLOR">Цвет фона</option>
                <option value="IMAGE">Изображение</option>
              </Form.Select>
              {form.backgroundType === "COLOR" && (
                <Form.Control
                  type="color"
                  value={form.backgroundColor}
                  disabled={!canEditRating}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, backgroundColor: e.target.value }))
                  }
                />
              )}
            </div>
            {form.backgroundType === "IMAGE" && canEditRating && (
              <div className={classes.row}>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleUpload(
                      (e.target as HTMLInputElement).files?.[0] ?? null,
                      "BACKGROUND",
                    )
                  }
                />
              </div>
            )}
            {form.backgroundType === "IMAGE" && form.backgroundMediaUrl && (
              <div className={classes.preview}>
                <img
                  src={mediaUrl(form.backgroundMediaUrl)}
                  alt=""
                  className={classes.previewItem}
                />
              </div>
            )}
          </div>

          <div className={classes.section}>
            <h5>QR-код</h5>
            <Form.Check
              type="switch"
              label="Показывать QR"
              checked={form.showQr}
              disabled={!canEditRating}
              onChange={(e) =>
                setForm((p) => ({ ...p, showQr: e.target.checked }))
              }
            />
            <Form.Group className="mt-2">
              <Form.Label>Адрес, по которому ведет QR-код</Form.Label>
              <Form.Control
                placeholder="Ссылка на внешний опрос"
                value={form.qrUrl ?? ""}
                disabled={!canEditRating}
                onChange={(e) =>
                  setForm((p) => ({ ...p, qrUrl: e.target.value }))
                }
              />
            </Form.Group>
            <Form.Group className="mt-2">
              <Form.Label>Текст рядом с QR</Form.Label>
              <Form.Control
                placeholder="QR код для прохождения опроса"
                value={form.qrLabelText}
                disabled={!canEditRating}
                onChange={(e) =>
                  setForm((p) => ({ ...p, qrLabelText: e.target.value }))
                }
              />
            </Form.Group>
          </div>

          <div className={classes.section}>
            <h5>Таймауты</h5>
            <p className={classes.hint}>
              Параметры времени для формы оценки, модалки продления и экрана
              благодарности. Значение 0 отключает соответствующий экран или таймер.
            </p>
            <div className={classes.row}>
              <Form.Label>Форма оценки</Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                min={0}
                step={1}
                value={form.ratingTimeoutSeconds}
                disabled={!canEditRating}
                onChange={(e) =>
                  setNonNegativeIntegerField(
                    "ratingTimeoutSeconds",
                    e.target.value,
                  )
                }
              />
            </div>
            <p className={classes.hint}>
              Сколько секунд бездействия на форме оценки, после чего открывается
              модальное окно «Нужно больше времени?». 0 — окно не показывается.
            </p>
            <div className={classes.row}>
              <Form.Label>
                Модальное окно «Нужно больше времени?» (сек)
              </Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                min={0}
                step={1}
                value={form.extensionCountdownSeconds}
                disabled={!canEditRating}
                onChange={(e) =>
                  setNonNegativeIntegerField(
                    "extensionCountdownSeconds",
                    e.target.value,
                  )
                }
              />
            </div>
            <p className={classes.hint}>
              Таймер на модальном окне «Нужно больше времени?». По истечении срока без
              ответа считается отказ и показывается скринсейвер. 0 — окно не показывается.
            </p>
            <div className={classes.row}>
              <Form.Label>Экран благодарности (сек)</Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                min={0}
                step={1}
                value={form.thankYouSeconds}
                disabled={!canEditRating}
                onChange={(e) =>
                  setNonNegativeIntegerField("thankYouSeconds", e.target.value)
                }
              />
            </div>
            <p className={classes.hint}>
              Сколько секунд показывается экран благодарности после
              отправки оценки, затем включается скринсейвер. 0 — экран не показывается.
            </p>
          </div>

          <div className={classes.section}>
            <h5>Скринсейвер</h5>
            <p className={classes.hint}>
              Выберите этапы обслуживания, на которых может отображаться
              скринсейвер. Если талона нет, скринсейвер показывается всегда.
            </p>
            <div className={classes.phaseGroup}>
              {IN_SERVICE_TICKET_PHASES.map(({ value, label, hint }) => (
                <Form.Check
                  key={`screensaver-${value}`}
                  type="checkbox"
                  id={`screensaver-${value}`}
                  label={
                    <span>
                      {label}
                      <span className={classes.phaseHint}> — {hint}</span>
                    </span>
                  }
                  checked={form.screensaverPhases?.includes(value) ?? false}
                  disabled={!canEditRating}
                  onChange={() => togglePhase("screensaverPhases", value)}
                />
              ))}
            </div>
            <div className={classes.row}>
              <Form.Label>Бездействие на экране «Обслуживание в процессе» (сек)</Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                min={0}
                step={1}
                value={form.screensaverIdleSeconds}
                disabled={!canEditRating}
                onChange={(e) =>
                  setNonNegativeIntegerField(
                    "screensaverIdleSeconds",
                    e.target.value,
                  )
                }
              />
            </div>
            <p className={classes.hint}>
              Если на планшете экран «Обслуживание в процессе» и нет касаний столько секунд — показывается скринсейвер. 0 — таймер отключён.
            </p>
            <div className={classes.row}>
              <Form.Label>Показ изображения в скринсейвере (сек)</Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                min={0}
                step={1}
                value={form.screensaverImageSeconds}
                disabled={!canEditRating}
                onChange={(e) =>
                  setNonNegativeIntegerField(
                    "screensaverImageSeconds",
                    e.target.value,
                  )
                }
              />
            </div>
            <p className={classes.hint}>
              Сколько секунд показывается каждое изображение в карусели скринсейвера
              перед переходом к следующему. 0 — смена только по окончании видео.
            </p>
            {canEditRating && (
              <>
                <Form.Label className="mt-2">Выберите файл</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*,video/*"
                  onChange={(e) =>
                    handleUpload(
                      (e.target as HTMLInputElement).files?.[0] ?? null,
                      "SCREENSAVER",
                    )
                  }
                />
              </>
            )}
            {orderedScreensaverMedia.length > 0 && (
              <p className={classes.hint}>
                Перетащите элементы для изменения порядка показа. Порядок
                сохраняется кнопкой «Сохранить».
              </p>
            )}
            <div className={classes.screensaverList}>
              {orderedScreensaverMedia.map((m, index) => (
                <div
                  key={m.id}
                  className={classes.screensaverItem}
                  draggable={canEditRating}
                  onDragStart={() =>
                    canEditRating && setScreensaverDragIndex(index)
                  }
                  onDragOver={(e) => {
                    if (!canEditRating) {
                      return;
                    }
                    e.preventDefault();
                    e.currentTarget.classList.add(
                      classes.screensaverItemDragOver,
                    );
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.classList.remove(
                      classes.screensaverItemDragOver,
                    );
                  }}
                  onDrop={(e) => {
                    if (!canEditRating) {
                      return;
                    }
                    e.preventDefault();
                    e.currentTarget.classList.remove(
                      classes.screensaverItemDragOver,
                    );
                    if (
                      screensaverDragIndex === null ||
                      screensaverDragIndex === index
                    ) {
                      setScreensaverDragIndex(null);
                      return;
                    }
                    moveScreensaver(screensaverDragIndex, index);
                    setScreensaverDragIndex(null);
                  }}
                  onDragEnd={() => setScreensaverDragIndex(null)}
                >
                  <span className={classes.screensaverOrder}>{index + 1}</span>
                  <div className={classes.screensaverPreview}>
                    {m.mediaType === "VIDEO" ? (
                      <video
                        src={mediaUrl(m.url)}
                        muted
                        className={classes.previewItem}
                      />
                    ) : (
                      <img
                        src={mediaUrl(m.url)}
                        alt=""
                        className={classes.previewItem}
                      />
                    )}
                  </div>
                  {canEditRating && (
                    <div className={classes.screensaverActions}>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={index === 0}
                        onClick={() => moveScreensaver(index, index - 1)}
                        title="Вверх"
                      >
                        ↑
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={index === orderedScreensaverMedia.length - 1}
                        onClick={() => moveScreensaver(index, index + 1)}
                        title="Вниз"
                      >
                        ↓
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        disabled={deleteMedia.isLoading}
                        onClick={() => handleDeleteScreensaver(m.id)}
                      >
                        Удалить
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className={classes.section}>
            <h5>Ссылка на планшет</h5>
            <p className={classes.hint}>
              Адрес страницы оценки для конкретного рабочего места. Параметры{" "}
              <code>branch</code> и <code>queue</code> подставляются из
              выбранного филиала и очереди; укажите номера кабинета и окна,
              чтобы ссылка указывала на нужное окно обслуживания.
            </p>
            {selectedBranchId != null && selectedQueueName && (
              <p className={classes.hint}>
                Филиал (<code>branch</code>):{" "}
                <strong>{selectedBranchId}</strong>
                {" · "}
                Очередь (<code>queue</code>):{" "}
                <strong>{selectedQueueName}</strong>
              </p>
            )}
            <div className={classes.linkParams}>
              <div className={classes.linkParamField}>
                <Form.Label>
                  Кабинет (<code>cabinet</code>)
                </Form.Label>
                <Form.Control
                  placeholder="1 или -"
                  value={previewCabinet}
                  onChange={(e) => setPreviewCabinet(e.target.value)}
                />
                <span className={classes.linkParamHint}>
                  Номер кабинета рабочего места. «-» — если не задан.
                </span>
              </div>
              <div className={classes.linkParamField}>
                <Form.Label>
                  Окно (<code>window</code>)
                </Form.Label>
                <Form.Control
                  placeholder="1 или -"
                  value={previewWindowNum}
                  onChange={(e) => setPreviewWindowNum(e.target.value)}
                />
                <span className={classes.linkParamHint}>
                  Номер окна обслуживания. «-» — если не задан.
                </span>
              </div>
            </div>
            {ratingLink && (
              <>
                <Form.Label className="mb-1">Ссылка</Form.Label>
                <div className={classes.linkBox}>{ratingLink}</div>
              </>
            )}
          </div>

          {canEditRating && (
            <div className={classes.actions}>
              <CustomButton
                content={
                  putSettings.isLoading || isLoading
                    ? "Сохранение..."
                    : "Сохранить"
                }
                styleType="active"
                onClick={() => {
                  if (putSettings.isLoading || isLoading) {
                    return;
                  }
                  handleSave();
                }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RatingSettingsPage;
